import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useFieldLibraryDocs } from '@/hooks/useFieldLibraryDocs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Database, Hash, Type, ListOrdered, ToggleLeft, Box, FileText, ArrowRight, Menu } from 'lucide-react';

const typeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  string: Type,
  number: Hash,
  integer: ListOrdered,
  boolean: ToggleLeft,
  array: Box,
  object: FileText,
};

const typeOrder = ['string', 'number', 'integer', 'boolean', 'array', 'object'];

export default function FieldLibraryIndex() {
  const { data, isLoading, error } = useFieldLibraryDocs();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const totalFields = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.groupedByType).reduce((sum, fields) => sum + fields.length, 0);
  }, [data]);

  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!data) return [];
    
    return typeOrder
      .filter((type) => data.groupedByType[type]?.length > 0)
      .map((type) => ({
        title: type.charAt(0).toUpperCase() + type.slice(1),
        slug: type,
        items: data.groupedByType[type]?.map((field) => ({
          title: field.name,
          slug: field.slug,
          href: `/docs/field-library/${field.slug}`,
        })) || [],
      }));
  }, [data]);
  
  return (
    <Layout>
      <SEOHead path="/docs/field-library" />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">Field Library</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Reusable field definitions used across data standards. Each field includes 
                type information, validation constraints, and format specifications.
              </p>
            </div>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-8">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <Skeleton className="h-6 w-40 mb-4" />
                    <div className="grid gap-4 md:grid-cols-2">
                      <Skeleton className="h-24" />
                      <Skeleton className="h-24" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <p className="text-destructive">Failed to load field library. Please try again.</p>
              </div>
            )}
            
            {/* Empty State */}
            {data && totalFields === 0 && (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No fields in library</h3>
                <p className="text-muted-foreground">
                  Check back soon for field definitions.
                </p>
              </div>
            )}
            
            {/* Field Types */}
            {data && typeOrder.map((type) => {
              const fields = data.groupedByType[type];
              if (!fields || fields.length === 0) return null;
              
              const TypeIcon = typeIcons[type] || Type;
              
              return (
                <section key={type} className="mb-10">
                  <div className="flex items-center gap-2 mb-4">
                    <TypeIcon className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">{type.charAt(0).toUpperCase() + type.slice(1)}</h2>
                    <Badge variant="secondary" className="ml-2">
                      {fields.length} {fields.length === 1 ? 'field' : 'fields'}
                    </Badge>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    {fields.map((field) => (
                      <Link key={field.id} to={`/docs/field-library/${field.slug}`} className="group">
                        <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                          <CardHeader className="pb-4">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <CardTitle className="text-base flex items-center gap-2">
                                  {field.name}
                                  {field.is_common && (
                                    <Badge variant="outline" className="text-xs">common</Badge>
                                  )}
                                  <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                                </CardTitle>
                                <code className="text-xs text-muted-foreground">
                                  {field.slug}
                                </code>
                                {field.description && (
                                  <CardDescription className="text-sm line-clamp-2 mt-2">
                                    {field.description}
                                  </CardDescription>
                                )}
                              </div>
                              <Badge variant="secondary" className="shrink-0">
                                {field.data_type}
                              </Badge>
                            </div>
                          </CardHeader>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle field library menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold mb-4 px-3">Field Library</h3>
            <DocsSidebarNav 
              sections={sidebarSections} 
              onItemClick={() => setSidebarOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
